import { useEffect, useRef, useState } from 'react';
import { ArrowLeft, AlertCircle, Banknote, Check, CheckCircle, ChevronDown, ClipboardCheck, Clock, FileText, Loader2, Plus, Save, Search, Trash2, X } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { detectPerms } from '../../permissions';
import { LeadTicketsSection } from './LeadTicketsSection';
import { LeadStepsSection } from './LeadStepsSection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LEAD_STATUSES = ['Open', 'In Progress', 'Complete', 'Closed Won', 'Closed Lost'];

const STATUS_STYLES = {
  Open: 'bg-blue-100 text-blue-700 border-blue-200',
  'In Progress': 'bg-amber-100 text-amber-700 border-amber-200',
  Complete: 'bg-cyan-100 text-cyan-700 border-cyan-200',
  'Closed Won': 'bg-green-100 text-green-700 border-green-200',
  'Closed Lost': 'bg-red-100 text-red-700 border-red-200',
};

const QUOTATION_STATUS_STYLES = {
  Open: 'bg-blue-100 text-blue-700',
  Approved: 'bg-green-100 text-green-700',
  Rejected: 'bg-red-100 text-red-700',
  Cancelled: 'bg-slate-100 text-slate-600',
};

const INSPECTION_STATUSES = ['Open', 'Assigned', 'In Progress', 'Complete'];

const emptyQuotationItem = () => ({
  item_id: null,
  service_item: '',
  description: '',
  cost: '',
  tax_id: '',
});

const initialQuotationForm = {
  subject: '',
  quotation_date: '',
  valid_until: '',
  notes: '',
};

const formatDateTime = (value) => value
  ? new Date(value).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  : '-';

const parseListValues = (value) => (value || '')
  .split(',')
  .map((item) => item.trim())
  .filter(Boolean);

const formatCurrency = (value) => new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 2,
}).format(Number(value) || 0);

const ProductPicker = ({ products, value, initialLabel, onSearch, onSelect, testId }) => {
  const containerRef = useRef(null);
  const selectedProduct = products.find((product) => product.id === value);
  const [query, setQuery] = useState(selectedProduct?.name || initialLabel || '');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setQuery(selectedProduct?.name || initialLabel || '');
  }, [initialLabel, selectedProduct?.name]);

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (!containerRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, []);

  const normalizedQuery = query.trim().toLowerCase();
  const filteredProducts = products.filter((product) => {
    if (!normalizedQuery) return true;
    return product.name?.toLowerCase().includes(normalizedQuery)
      || product.product_code?.toLowerCase().includes(normalizedQuery);
  });

  return (
    <div ref={containerRef} className="relative">
      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
      <input type="text" value={query} data-testid={testId}
        onFocus={() => setOpen(true)}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
          onSearch(event.target.value);
        }}
        placeholder="Type product name or code..."
        className="w-full pl-9 pr-8 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
      <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
      {open && (
        <div className="absolute z-30 left-0 right-0 mt-1 max-h-56 overflow-y-auto bg-white border border-slate-200 rounded-sm shadow-lg">
          {filteredProducts.length === 0 ? (
            <div className="px-3 py-3 text-sm text-slate-500">No products found</div>
          ) : filteredProducts.map((product) => (
            <button key={product.id} type="button"
              onClick={() => {
                setQuery(product.name);
                setOpen(false);
                onSelect(product);
              }}
              className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left hover:bg-slate-50 ${value === product.id ? 'bg-orange-50' : ''}`}>
              <span className="min-w-0">
                <span className="block text-sm font-medium text-slate-900 truncate">{product.name}</span>
                {product.product_code && <span className="block text-xs text-slate-500 font-mono">{product.product_code}</span>}
              </span>
              {value === product.id && <Check className="h-4 w-4 shrink-0 text-orange-500" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const DetailValue = ({ label, value, className = '' }) => (
  <div className={className}>
    <span className="block text-xs text-slate-500 uppercase tracking-wider mb-1">{label}</span>
    <span className="block text-sm text-slate-800 whitespace-pre-wrap">{value || '-'}</span>
  </div>
);

const FlagRow = ({ label, enabled }) => (
  <div className="flex items-center justify-between gap-3">
    <span className="text-sm text-slate-700">{label}</span>
    <span className={`px-2.5 py-1 rounded text-xs font-semibold ${enabled ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
      {enabled ? 'Yes' : 'No'}
    </span>
  </div>
);

export const LeadDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const perms = detectPerms();
  const [lead, setLead] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('Open');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [receivablesMaster, setReceivablesMaster] = useState([]);
  const [receivableModalOpen, setReceivableModalOpen] = useState(false);
  const [selectedReceivables, setSelectedReceivables] = useState([]);
  const [receivablesSaving, setReceivablesSaving] = useState(false);
  const [receivablesError, setReceivablesError] = useState('');
  const [quotations, setQuotations] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [products, setProducts] = useState([]);
  const [serviceAssignees, setServiceAssignees] = useState([]);
  const [quotationModalOpen, setQuotationModalOpen] = useState(false);
  const [quotationForm, setQuotationForm] = useState(initialQuotationForm);
  const [quotationItems, setQuotationItems] = useState([emptyQuotationItem()]);
  const [quotationSaving, setQuotationSaving] = useState(false);
  const [quotationError, setQuotationError] = useState('');
  const [approvingQuotationId, setApprovingQuotationId] = useState(null);
  const [inspectionModalOpen, setInspectionModalOpen] = useState(false);
  const [inspectionForm, setInspectionForm] = useState({ assignee_id: '', status: 'Open' });
  const [inspectionSaving, setInspectionSaving] = useState(false);
  const [inspectionError, setInspectionError] = useState('');

  const authConfig = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  });

  useEffect(() => {
    const fetchLead = async () => {
      try {
        const [leadResponse, receivablesResponse, quotationsResponse, taxesResponse, productsResponse, usersResponse] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/leads/${id}`, authConfig()),
          axios.get(`${BACKEND_URL}/api/receivables`, authConfig()).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/quotations/by-lead/${id}`, authConfig()).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/taxes`, authConfig()).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/products`, authConfig()).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/users`, authConfig()).catch(() => ({ data: [] })),
        ]);
        setLead(leadResponse.data);
        setSelectedStatus(leadResponse.data.status);
        setReceivablesMaster(receivablesResponse.data || []);
        setQuotations(quotationsResponse.data || []);
        setTaxes(taxesResponse.data || []);
        setProducts((productsResponse.data || []).filter((product) => product.active !== false));
        setServiceAssignees((usersResponse.data || []).filter(
          (user) => user.active
            && (user.role_name === 'Service Technician' || user.role_name === 'Service Manager'),
        ));
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load lead details');
      } finally {
        setLoading(false);
      }
    };
    fetchLead();
  }, [id]);

  const updateStatus = async () => {
    if (!lead || selectedStatus === lead.status) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const response = await axios.patch(
        `${BACKEND_URL}/api/leads/${id}/status`,
        { status: selectedStatus },
        authConfig(),
      );
      setLead(response.data);
      setSelectedStatus(response.data.status);
      setSuccess('Lead status updated successfully.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update lead status');
    } finally {
      setSaving(false);
    }
  };

  const openReceivableModal = () => {
    setSelectedReceivables(parseListValues(lead.receivables));
    setReceivablesError('');
    setReceivableModalOpen(true);
  };

  const toggleReceivable = (name) => {
    setSelectedReceivables((current) => current.includes(name)
      ? current.filter((item) => item !== name)
      : [...current, name]);
  };

  const saveReceivables = async () => {
    setReceivablesSaving(true);
    setReceivablesError('');
    setError('');
    setSuccess('');
    try {
      const response = await axios.put(
        `${BACKEND_URL}/api/leads/${id}`,
        { receivables: selectedReceivables.length ? selectedReceivables.join(', ') : null },
        authConfig(),
      );
      setLead(response.data);
      setReceivableModalOpen(false);
      setSuccess('Receivables updated successfully.');
    } catch (err) {
      setReceivablesError(err.response?.data?.detail || 'Failed to update receivables');
    } finally {
      setReceivablesSaving(false);
    }
  };

  const refreshLeadAndQuotations = async () => {
    const [leadResponse, quotationsResponse] = await Promise.all([
      axios.get(`${BACKEND_URL}/api/leads/${id}`, authConfig()),
      axios.get(`${BACKEND_URL}/api/quotations/by-lead/${id}`, authConfig()),
    ]);
    setLead(leadResponse.data);
    setSelectedStatus(leadResponse.data.status);
    setQuotations(quotationsResponse.data || []);
  };

  const openQuotationModal = () => {
    const currentQuotation = quotations[0];
    setQuotationError('');
    if (currentQuotation) {
      setQuotationForm({
        subject: currentQuotation.subject || '',
        quotation_date: currentQuotation.quotation_date || '',
        valid_until: currentQuotation.valid_until || '',
        notes: currentQuotation.notes || '',
      });
      setQuotationItems((currentQuotation.line_items || []).length
        ? currentQuotation.line_items.map((item) => ({
            item_id: item.item_id || null,
            service_item: item.service_item || '',
            description: item.description || '',
            cost: item.cost ?? '',
            tax_id: item.tax_id || '',
          }))
        : [emptyQuotationItem()]);
    } else {
      const leadProduct = products.find((product) => product.name === lead.product);
      setQuotationForm({
        ...initialQuotationForm,
        subject: `Quotation for ${lead.name}`,
        quotation_date: new Date().toISOString().slice(0, 10),
      });
      setQuotationItems([leadProduct ? {
        ...emptyQuotationItem(),
        item_id: leadProduct.id,
        service_item: leadProduct.name,
        cost: leadProduct.sale_rate ?? '',
        tax_id: leadProduct.tax_id || '',
      } : emptyQuotationItem()]);
    }
    setQuotationModalOpen(true);
  };

  const updateQuotationItem = (index, field, value) => {
    setQuotationItems((current) => current.map((item, itemIndex) => (
      itemIndex === index ? { ...item, [field]: value } : item
    )));
  };

  const selectQuotationProduct = (index, product) => {
    setQuotationItems((current) => current.map((item, itemIndex) => (
      itemIndex === index ? {
        ...item,
        item_id: product.id,
        service_item: product.name,
        cost: product.sale_rate ?? '',
        tax_id: product.tax_id || '',
      } : item
    )));
  };

  const saveQuotation = async (event) => {
    event.preventDefault();
    const typedWithoutSelection = quotationItems.some((item) => item.service_item.trim() && !products.some((product) => product.id === item.item_id));
    if (typedWithoutSelection) {
      setQuotationError('Select each product from the Product Master dropdown.');
      return;
    }
    const lineItems = quotationItems
      .map((item) => ({
        item_id: item.item_id || null,
        service_item: item.service_item.trim(),
        description: item.description?.trim() || null,
        cost: Number(item.cost) || 0,
        tax_id: item.tax_id || null,
      }))
      .filter((item) => item.service_item);

    if (!lineItems.length) {
      setQuotationError('Add at least one item or service.');
      return;
    }

    setQuotationSaving(true);
    setQuotationError('');
    setError('');
    setSuccess('');
    try {
      const payload = {
        ...quotationForm,
        quotation_date: quotationForm.quotation_date || null,
        valid_until: quotationForm.valid_until || null,
        notes: quotationForm.notes || null,
        line_items: lineItems,
      };
      if (quotations.length) {
        await axios.put(`${BACKEND_URL}/api/quotations/${quotations[0].id}`, payload, authConfig());
      } else {
        await axios.post(
          `${BACKEND_URL}/api/quotations`,
          { ...payload, lead_id: id, status: 'Open' },
          authConfig(),
        );
      }
      await refreshLeadAndQuotations();
      setQuotationModalOpen(false);
      setSuccess(quotations.length ? 'Quotation updated successfully.' : 'Quotation added successfully.');
    } catch (err) {
      setQuotationError(err.response?.data?.detail || 'Failed to save quotation');
    } finally {
      setQuotationSaving(false);
    }
  };

  const approveQuotation = async (quotation) => {
    setApprovingQuotationId(quotation.id);
    setError('');
    setSuccess('');
    try {
      await axios.put(
        `${BACKEND_URL}/api/quotations/${quotation.id}`,
        { status: 'Approved' },
        authConfig(),
      );
      await refreshLeadAndQuotations();
      setSuccess(`${quotation.quotation_number} approved successfully.`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to approve quotation');
    } finally {
      setApprovingQuotationId(null);
    }
  };

  const openInspectionModal = () => {
    setInspectionForm({
      assignee_id: lead.inspection_assignee_id
        || lead.inspection_technician_id
        || lead.inspection_manager_id
        || '',
      status: lead.inspection_status || 'Open',
    });
    setInspectionError('');
    setInspectionModalOpen(true);
  };

  const saveInspection = async (event) => {
    event.preventDefault();
    if (!inspectionForm.assignee_id) {
      setInspectionError('Select a service technician or service manager.');
      return;
    }
    setInspectionSaving(true);
    setInspectionError('');
    setError('');
    setSuccess('');
    try {
      const response = await axios.patch(
        `${BACKEND_URL}/api/leads/${id}/inspection`,
        inspectionForm,
        authConfig(),
      );
      setLead(response.data);
      setSelectedStatus(response.data.status);
      setInspectionModalOpen(false);
      setSuccess('Inspection updated successfully.');
    } catch (err) {
      setInspectionError(err.response?.data?.detail || 'Failed to update inspection');
    } finally {
      setInspectionSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>;
  }

  if (!lead) {
    return (
      <div>
        <button type="button" onClick={() => navigate('/admin/services/leads')}
          className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6">
          <ArrowLeft className="h-4 w-4" /> Back to Leads
        </button>
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm">
          <AlertCircle className="h-5 w-5" />{error || 'Lead not found'}
        </div>
      </div>
    );
  }

  const leadReceivables = parseListValues(lead.receivables);

  return (
    <div data-testid="lead-detail-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <button type="button" onClick={() => navigate('/admin/services/leads')} data-testid="back-btn"
            className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-3 transition-colors">
            <ArrowLeft className="h-4 w-4" /> Back to Leads
          </button>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">{lead.name}</h1>
            <span className={`inline-flex items-center px-3 py-1 rounded text-xs font-semibold border ${STATUS_STYLES[lead.status] || 'bg-slate-100 text-slate-700 border-slate-200'}`}
              data-testid="lead-status-badge">
              {lead.status}
            </span>
          </div>
          <p className="text-sm text-slate-500 mt-2">Created {formatDateTime(lead.created_at)}</p>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
          <AlertCircle className="h-5 w-5" /><span>{error}</span>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-sm mb-6">
          <CheckCircle className="h-5 w-5" /><span>{success}</span>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-6 items-start">
        <section className="bg-white rounded-sm border border-slate-200 p-6">
          <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-5">Lead Details</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <DetailValue label="Name" value={lead.name} />
            <DetailValue label="Lead Source" value={lead.lead_source_name} />
            <DetailValue label="Email" value={lead.email} />
            <DetailValue label="Phone Number" value={lead.phone} />
            <DetailValue label="Product" value={lead.product} />
            <DetailValue label="Last Updated" value={formatDateTime(lead.updated_at)} />
            <DetailValue label="Requirements" value={parseListValues(lead.requirements).join('\n')} className="sm:col-span-2" />
            <DetailValue label="Remarks" value={lead.remarks} className="sm:col-span-2" />
          </div>
        </section>

        <LeadStepsSection
          leadId={id}
          customerName={lead.name}
          customerPhone={lead.phone}
          serviceUsers={serviceAssignees}
          canEdit={perms.canEdit}
          className=""
        />

        <div className="space-y-6">
          <section className="bg-white rounded-sm border border-slate-200 p-6">
            <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-4">Update Status</h2>
            <label className="block text-sm font-medium text-slate-700 mb-1" htmlFor="lead-status">Status</label>
            <select id="lead-status" value={selectedStatus} onChange={(event) => { setSelectedStatus(event.target.value); setSuccess(''); }}
              disabled={!perms.canEdit || saving} data-testid="lead-status-select"
              className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none bg-white disabled:bg-slate-100 disabled:text-slate-500">
              {LEAD_STATUSES.map((status) => <option key={status} value={status}>{status}</option>)}
            </select>
            {perms.canEdit && (
              <button type="button" onClick={updateStatus} disabled={saving || selectedStatus === lead.status}
                data-testid="update-status-btn"
                className="w-full mt-3 inline-flex items-center justify-center gap-2 bg-orange-500 text-white font-bold uppercase tracking-wider px-4 py-2.5 rounded-sm hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Update Status
              </button>
            )}
          </section>

          <section className="bg-white rounded-sm border border-slate-200 p-6">
            <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-4">Lead Flags</h2>
            <div className="space-y-3">
              <FlagRow label="Items Received" enabled={lead.items_received} />
              <FlagRow label="Quotation Sent" enabled={lead.quotation_sent} />
            </div>
          </section>
        </div>
      </div>

      <LeadTicketsSection
        leadId={id}
        customerName={lead.name}
        customerPhone={lead.phone}
        quotations={quotations}
        serviceUsers={serviceAssignees}
        canEdit={perms.canEdit}
      />

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-6 items-start">
        <section className="bg-white rounded-sm border border-slate-200 p-6" data-testid="lead-receivables">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
            <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Receivables</h2>
            {perms.canEdit && (
              <button type="button" onClick={openReceivableModal} data-testid="add-lead-receivable-btn"
                className="inline-flex items-center justify-center gap-2 bg-orange-500 text-white font-bold uppercase tracking-wider px-4 py-2 rounded-sm hover:bg-orange-600 transition-colors">
                <Plus className="h-4 w-4" /> Add Receivable
              </button>
            )}
          </div>
          {leadReceivables.length === 0 ? (
            <p className="text-sm text-slate-500" data-testid="no-lead-receivables">N/A</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {leadReceivables.map((receivable) => (
                <span key={receivable}
                  className="inline-flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-sm text-sm font-medium">
                  <Banknote className="h-4 w-4" /> {receivable}
                </span>
              ))}
            </div>
          )}
        </section>

        <section className="bg-white rounded-sm border border-slate-200 p-6" data-testid="lead-inspection">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
            <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Inspection</h2>
            {perms.canEdit && (
              <button type="button" onClick={openInspectionModal} data-testid="inspection-btn"
                className="inline-flex items-center justify-center gap-2 bg-orange-500 text-white font-bold uppercase tracking-wider px-4 py-2 rounded-sm hover:bg-orange-600 transition-colors">
                <ClipboardCheck className="h-4 w-4" /> {lead.inspection_status ? 'Update Inspection' : 'Add Inspection'}
              </button>
            )}
          </div>
          {!lead.inspection_status ? (
            <p className="text-sm text-slate-500" data-testid="no-lead-inspection">N/A</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <DetailValue label="Status" value={lead.inspection_status} />
              <DetailValue
                label="Assigned To"
                value={lead.inspection_assignee_name
                  ? `${lead.inspection_assignee_name}${lead.inspection_assignee_role ? ` (${lead.inspection_assignee_role})` : ''}`
                  : lead.inspection_technician_name || lead.inspection_manager_name}
              />
            </div>
          )}
        </section>

        <section className="bg-white rounded-sm border border-slate-200 p-6" data-testid="lead-quotations">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
            <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Quotations</h2>
            {perms.canEdit && (
              <button type="button" onClick={openQuotationModal} data-testid="lead-quotation-action-btn"
                className="inline-flex items-center justify-center gap-2 bg-orange-500 text-white font-bold uppercase tracking-wider px-4 py-2 rounded-sm hover:bg-orange-600 transition-colors">
                {quotations.length ? <Save className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                {quotations.length ? 'Update Quotation' : 'Add Quotation'}
              </button>
            )}
          </div>

          {quotations.length === 0 ? (
            <p className="text-sm text-slate-500" data-testid="no-lead-quotations">N/A</p>
          ) : (
            <div className="space-y-3">
              {quotations.map((quotation) => (
                <div key={quotation.id} className="border border-slate-200 rounded-sm p-4" data-testid={`lead-quotation-${quotation.id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <FileText className="h-4 w-4 text-slate-400" />
                        <span className="font-mono font-bold text-sm text-slate-900">{quotation.quotation_number}</span>
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${QUOTATION_STATUS_STYLES[quotation.status] || 'bg-slate-100 text-slate-600'}`}>
                          {quotation.status}
                        </span>
                      </div>
                      <p className="text-sm text-slate-700 mt-2">{quotation.subject || 'Quotation'}</p>
                      <p className="text-xs text-slate-400 mt-1">{formatDateTime(quotation.updated_at)}</p>
                    </div>
                    {perms.canEdit && quotation.status !== 'Approved' && (
                      <button type="button" onClick={() => approveQuotation(quotation)}
                        disabled={approvingQuotationId === quotation.id}
                        data-testid={`approve-quotation-${quotation.id}`}
                        className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-green-600 text-white text-xs font-bold uppercase tracking-wider rounded-sm hover:bg-green-700 disabled:opacity-50">
                        {approvingQuotationId === quotation.id
                          ? <Loader2 className="h-4 w-4 animate-spin" />
                          : <CheckCircle className="h-4 w-4" />}
                        Approve
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-3 gap-3 mt-4 pt-3 border-t border-slate-100">
                    <div>
                      <span className="block text-xs text-slate-500">Subtotal</span>
                      <span className="text-sm font-medium text-slate-800">{formatCurrency(quotation.subtotal)}</span>
                    </div>
                    <div>
                      <span className="block text-xs text-slate-500">Tax</span>
                      <span className="text-sm font-medium text-slate-800">{formatCurrency(quotation.tax_total)}</span>
                    </div>
                    <div>
                      <span className="block text-xs text-slate-500">Total</span>
                      <span className="text-sm font-bold text-slate-900">{formatCurrency(quotation.grand_total)}</span>
                    </div>
                  </div>

                  {(quotation.line_items || []).length > 0 && (
                    <div className="space-y-1.5 mt-3 pt-3 border-t border-slate-100">
                      {quotation.line_items.map((item, index) => (
                        <div key={`${quotation.id}-item-${index}`} className="flex items-center justify-between gap-3 text-sm">
                          <span className="text-slate-600 truncate">{item.service_item}</span>
                          <span className="font-medium text-slate-800 shrink-0">{formatCurrency(item.total)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      <section className="bg-white rounded-sm border border-slate-200 p-6" data-testid="lead-activity">
          <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-5">Activity Timeline</h2>
          {(lead.activity || []).length === 0 ? (
            <p className="text-sm text-slate-500">No activity recorded yet.</p>
          ) : (
            <div className="space-y-4">
              {[...lead.activity].reverse().map((activity, index) => (
                <div key={`${activity.timestamp}-${index}`} className="flex gap-3">
                  <div className="mt-0.5 h-8 w-8 shrink-0 rounded-full bg-slate-100 flex items-center justify-center">
                    <Clock className="h-4 w-4 text-slate-500" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900">
                      {activity.action}
                      {activity.from_status && activity.to_status && (
                        <span className="font-normal text-slate-600"> · {activity.from_status} → {activity.to_status}</span>
                      )}
                    </p>
                    {activity.details && <p className="text-sm text-slate-600 mt-0.5">{activity.details}</p>}
                    <p className="text-xs text-slate-400 mt-1">{formatDateTime(activity.timestamp)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
      </section>

      {inspectionModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="inspection-modal">
          <div className="bg-white rounded-sm w-full max-w-lg">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h2 className="text-xl font-bold text-slate-900 uppercase tracking-tight">Inspection</h2>
                <p className="text-sm text-slate-500 mt-1">Assign the inspection and update its status.</p>
              </div>
              <button type="button" onClick={() => setInspectionModalOpen(false)} disabled={inspectionSaving}
                aria-label="Close" className="p-2 text-slate-400 hover:text-slate-600 disabled:opacity-50">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={saveInspection} className="p-6">
              {inspectionError && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-5">
                  <AlertCircle className="h-5 w-5 shrink-0" /><span>{inspectionError}</span>
                </div>
              )}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1" htmlFor="inspection-assignee">
                    Assigned To <span className="text-red-500">*</span>
                  </label>
                  <select id="inspection-assignee" value={inspectionForm.assignee_id}
                    onChange={(event) => setInspectionForm((current) => ({ ...current, assignee_id: event.target.value }))}
                    data-testid="inspection-assignee-select"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm bg-white focus:border-orange-500 focus:outline-none">
                    <option value="">Select service technician or manager</option>
                    {serviceAssignees.map((user) => (
                      <option key={user.id} value={user.id}>{user.name} ({user.role_name})</option>
                    ))}
                  </select>
                  {serviceAssignees.length === 0 && <p className="text-xs text-amber-600 mt-1">No active Service Technician or Service Manager users found.</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1" htmlFor="inspection-status">Status</label>
                  <select id="inspection-status" value={inspectionForm.status}
                    onChange={(event) => setInspectionForm((current) => ({ ...current, status: event.target.value }))}
                    data-testid="inspection-status-select"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm bg-white focus:border-orange-500 focus:outline-none">
                    {INSPECTION_STATUSES.map((status) => <option key={status} value={status}>{status}</option>)}
                  </select>
                </div>
              </div>
              <div className="flex gap-3 justify-end pt-6 mt-6 border-t border-slate-200">
                <button type="button" onClick={() => setInspectionModalOpen(false)} disabled={inspectionSaving}
                  className="px-5 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50 disabled:opacity-50">
                  Cancel
                </button>
                <button type="submit" disabled={inspectionSaving || !serviceAssignees.length}
                  data-testid="save-inspection-btn"
                  className="inline-flex items-center gap-2 px-5 py-2 bg-orange-500 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 disabled:opacity-50">
                  {inspectionSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Save Inspection
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {quotationModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 overflow-y-auto" data-testid="lead-quotation-modal">
          <div className="bg-white rounded-sm w-full max-w-5xl my-8">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h2 className="text-xl font-bold text-slate-900 uppercase tracking-tight">
                  {quotations.length ? 'Update Quotation' : 'Add Quotation'}
                </h2>
                {quotations.length > 0 && (
                  <p className="text-sm text-slate-500 mt-1">Editing {quotations[0].quotation_number}</p>
                )}
              </div>
              <button type="button" onClick={() => setQuotationModalOpen(false)} disabled={quotationSaving}
                aria-label="Close" className="p-2 text-slate-400 hover:text-slate-600 disabled:opacity-50">
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={saveQuotation} className="p-6">
              {quotationError && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-5">
                  <AlertCircle className="h-5 w-5 shrink-0" /><span>{quotationError}</span>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="md:col-span-3">
                  <label className="block text-sm font-medium text-slate-700 mb-1" htmlFor="quotation-subject">Subject</label>
                  <input id="quotation-subject" type="text" value={quotationForm.subject}
                    onChange={(event) => setQuotationForm((current) => ({ ...current, subject: event.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1" htmlFor="quotation-date">Quotation Date</label>
                  <input id="quotation-date" type="date" value={quotationForm.quotation_date}
                    onChange={(event) => setQuotationForm((current) => ({ ...current, quotation_date: event.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1" htmlFor="quotation-valid-until">Valid Until</label>
                  <input id="quotation-valid-until" type="date" value={quotationForm.valid_until}
                    onChange={(event) => setQuotationForm((current) => ({ ...current, valid_until: event.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
                </div>
              </div>

              <div className="border border-slate-200 rounded-sm text-sm">
                <div className="hidden lg:grid lg:grid-cols-[40px_minmax(190px,1.2fr)_minmax(150px,1fr)_110px_160px_120px_40px] bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-600">
                  <span className="px-3 py-3">#</span>
                  <span className="px-3 py-3">Product</span>
                  <span className="px-3 py-3">Description</span>
                  <span className="px-3 py-3 text-right">Cost</span>
                  <span className="px-3 py-3">Tax</span>
                  <span className="px-3 py-3 text-right">Total</span>
                  <span></span>
                </div>
                {quotationItems.map((item, index) => {
                  const cost = Number(item.cost) || 0;
                  const tax = taxes.find((entry) => entry.id === item.tax_id);
                  const total = cost + (cost * (Number(tax?.percentage) || 0) / 100);
                  return (
                    <div key={index}
                      className="grid grid-cols-1 lg:grid-cols-[40px_minmax(190px,1.2fr)_minmax(150px,1fr)_110px_160px_120px_40px] gap-3 lg:gap-0 p-4 lg:p-0 border-b border-slate-100 last:border-0">
                      <div className="hidden lg:block px-3 py-3 text-slate-400">{index + 1}</div>
                      <div className="lg:px-3 lg:py-3">
                        <span className="block lg:hidden text-xs text-slate-500 mb-1">Product</span>
                        <ProductPicker products={products} value={item.item_id}
                          initialLabel={item.service_item}
                          onSearch={(value) => {
                            setQuotationItems((current) => current.map((currentItem, itemIndex) => (
                              itemIndex === index ? { ...currentItem, item_id: null, service_item: value } : currentItem
                            )));
                          }}
                          onSelect={(product) => selectQuotationProduct(index, product)}
                          testId={`quotation-item-${index}`} />
                      </div>
                      <div className="lg:px-3 lg:py-3">
                        <span className="block lg:hidden text-xs text-slate-500 mb-1">Description</span>
                        <input type="text" value={item.description || ''}
                          onChange={(event) => updateQuotationItem(index, 'description', event.target.value)}
                          placeholder="Description"
                          className="w-full px-2 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
                      </div>
                      <div className="lg:px-3 lg:py-3">
                        <span className="block lg:hidden text-xs text-slate-500 mb-1">Cost</span>
                        <input type="number" min="0" step="0.01" value={item.cost}
                          onChange={(event) => updateQuotationItem(index, 'cost', event.target.value)}
                          data-testid={`quotation-cost-${index}`}
                          className="w-full px-2 py-2 border border-slate-200 rounded-sm text-right focus:border-orange-500 focus:outline-none" />
                      </div>
                      <div className="lg:px-3 lg:py-3">
                        <span className="block lg:hidden text-xs text-slate-500 mb-1">Tax</span>
                        <select value={item.tax_id || ''}
                          onChange={(event) => updateQuotationItem(index, 'tax_id', event.target.value)}
                          className="w-full px-2 py-2 border border-slate-200 rounded-sm bg-white focus:border-orange-500 focus:outline-none">
                          <option value="">No Tax</option>
                          {taxes.filter((entry) => entry.active !== false).map((entry) => (
                            <option key={entry.id} value={entry.id}>{entry.name} ({entry.percentage}%)</option>
                          ))}
                        </select>
                      </div>
                      <div className="lg:px-3 lg:py-5 lg:text-right">
                        <span className="lg:hidden text-xs text-slate-500 mr-2">Total</span>
                        <span className="font-semibold text-slate-900">{formatCurrency(total)}</span>
                      </div>
                      <div className="lg:px-1 lg:py-4">
                        {quotationItems.length > 1 && (
                          <button type="button" onClick={() => setQuotationItems((current) => current.filter((_, itemIndex) => itemIndex !== index))}
                            aria-label="Remove item" className="p-1.5 text-slate-400 hover:text-red-500">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
                <div className="flex items-center justify-end gap-4 bg-slate-50 border-t-2 border-slate-200 px-4 py-3">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-600">Grand Total</span>
                  <span className="font-bold text-slate-900" data-testid="quotation-grand-total">
                    {formatCurrency(quotationItems.reduce((sum, item) => {
                      const cost = Number(item.cost) || 0;
                      const tax = taxes.find((entry) => entry.id === item.tax_id);
                      return sum + cost + (cost * (Number(tax?.percentage) || 0) / 100);
                    }, 0))}
                  </span>
                </div>
              </div>

              <button type="button" onClick={() => setQuotationItems((current) => [...current, emptyQuotationItem()])}
                data-testid="add-quotation-item-btn"
                className="inline-flex items-center gap-2 text-sm font-medium text-orange-600 hover:text-orange-700 mt-3">
                <Plus className="h-4 w-4" /> Add Item / Service
              </button>

              <div className="mt-5">
                <label className="block text-sm font-medium text-slate-700 mb-1" htmlFor="quotation-notes">Notes</label>
                <textarea id="quotation-notes" rows={3} value={quotationForm.notes}
                  onChange={(event) => setQuotationForm((current) => ({ ...current, notes: event.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none resize-none" />
              </div>

              <div className="flex gap-3 justify-end pt-6 mt-6 border-t border-slate-200">
                <button type="button" onClick={() => setQuotationModalOpen(false)} disabled={quotationSaving}
                  className="px-5 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50 disabled:opacity-50">
                  Cancel
                </button>
                <button type="submit" disabled={quotationSaving} data-testid="save-lead-quotation-btn"
                  className="inline-flex items-center gap-2 px-5 py-2 bg-orange-500 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 disabled:opacity-50">
                  {quotationSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  {quotations.length ? 'Update Quotation' : 'Add Quotation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {receivableModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="lead-receivable-modal">
          <div className="bg-white rounded-sm w-full max-w-lg max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h2 className="text-xl font-bold text-slate-900 uppercase tracking-tight">Add Receivable</h2>
                <p className="text-sm text-slate-500 mt-1">Select the receivables for this lead.</p>
              </div>
              <button type="button" onClick={() => setReceivableModalOpen(false)} disabled={receivablesSaving}
                aria-label="Close" className="p-2 text-slate-400 hover:text-slate-600 disabled:opacity-50">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto">
              {receivablesError && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-4">
                  <AlertCircle className="h-5 w-5 shrink-0" /><span>{receivablesError}</span>
                </div>
              )}
              {receivablesMaster.length === 0 ? (
                <p className="text-sm text-slate-500">No receivables configured. Add them from Service Hub → Receivables.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {receivablesMaster.map((receivable) => (
                    <label key={receivable.id}
                      className="flex items-center gap-3 px-3 py-3 border border-slate-200 rounded-sm hover:bg-slate-50 cursor-pointer">
                      <input type="checkbox" checked={selectedReceivables.includes(receivable.name)}
                        onChange={() => toggleReceivable(receivable.name)}
                        data-testid={`lead-receivable-${receivable.id}`}
                        className="h-4 w-4 rounded border-slate-300 text-orange-500 focus:ring-orange-500" />
                      <span className="text-sm text-slate-700">{receivable.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-3 justify-end px-6 py-4 border-t border-slate-200">
              <button type="button" onClick={() => setReceivableModalOpen(false)} disabled={receivablesSaving}
                className="px-5 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50 disabled:opacity-50">
                Cancel
              </button>
              <button type="button" onClick={saveReceivables} disabled={receivablesSaving}
                data-testid="save-lead-receivables-btn"
                className="inline-flex items-center gap-2 px-5 py-2 bg-orange-500 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 disabled:opacity-50">
                {receivablesSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
